import { WOO_ORDER_STATUSES, getWooOrderStatus } from '@mapara/core';
import { Button, Field, Input, SectionLabel, Select } from '../../ui/index.js';
import { VIEWS, money, relative } from './presentation.js';
import type { QueueView, ShopOrdersResponse } from './types.js';

export interface QueueFilters {
  readonly view: QueueView;
  readonly assigned: 'all' | 'me' | 'unassigned';
  readonly wooStatus: string;
  readonly search: string;
}

export interface QueueRailProps {
  readonly filters: QueueFilters;
  readonly onChange: (patch: Partial<QueueFilters>) => void;
  readonly data: ShopOrdersResponse | undefined;
  readonly onSync: () => void;
  readonly syncing: boolean;
  readonly searchRef: React.RefObject<HTMLInputElement | null>;
}

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  readonly label: string;
  readonly value: string;
  readonly sub: string;
  readonly tone: 'amber' | 'emerald' | 'blue' | 'red';
}): React.ReactElement {
  const tones = {
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
  };
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${tones[tone]}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</p>
      <p className="mt-0.5 text-xl font-bold tabular-nums">{value}</p>
      <p className="text-[10px] opacity-60">{sub}</p>
    </div>
  );
}

export function QueueRail({
  filters,
  onChange,
  data,
  onSync,
  syncing,
  searchRef,
}: QueueRailProps): React.ReactElement {
  const counts = data?.tabCounts ?? {};
  const total = data?.total ?? 0;
  const pending = counts.pending ?? 0;
  const callbacks = counts.callbacks ?? 0;
  const confirmed = counts.confirmed ?? 0;
  const unreachable = counts.unreachable ?? 0;

  return (
    <aside className="flex w-72 shrink-0 flex-col gap-4 overflow-y-auto border-r border-border bg-gradient-to-b from-panel/60 to-transparent px-3 py-4">
      <div>
        <SectionLabel>File d&apos;appel</SectionLabel>
        <p className="mt-1 text-3xl font-bold tabular-nums text-ink">{total}</p>
        <p className="text-xs text-ink-faint">
          {data === undefined ? 'chargement...' : `${data.totalStored} commandes en base`}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <KpiCard label="A appeler" value={String(pending)} sub="en attente" tone="amber" />
        <KpiCard label="Rappels" value={String(callbacks)} sub="programmes" tone="blue" />
        <KpiCard label="Confirmees" value={String(confirmed)} sub="aujourd'hui" tone="emerald" />
        <KpiCard label="Injoignables" value={String(unreachable)} sub="bloquees" tone="red" />
      </div>

      <div>
        <SectionLabel>Files</SectionLabel>
        <nav className="mt-1.5 flex flex-col gap-0.5" aria-label="Files">
          {VIEWS.map((item) => {
            const active = filters.view === item.key;
            const count = counts[item.key];
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => onChange({ view: item.key })}
                aria-current={active ? 'page' : undefined}
                className={[
                  'flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-all',
                  active
                    ? 'bg-herb-600 text-white shadow-sm'
                    : 'text-ink-soft hover:bg-panel hover:text-ink',
                ].join(' ')}
              >
                <span className="flex items-center gap-2 font-medium">
                  <span className="text-xs">{item.icon === 'phone' ? '📞' : item.icon === 'phone-off' ? '📵' : item.icon === 'clock' ? '🕐' : item.icon === 'check' ? '✓' : item.icon === 'truck' ? '🚚' : item.icon === 'x' ? '✕' : '☰'}</span>
                  {item.label}
                </span>
                <span className={active ? 'text-white/80 tabular-nums' : 'text-ink-faint tabular-nums'}>
                  {count ?? 0}
                </span>
              </button>
            );
          })}
        </nav>
      </div>

      <div className="flex flex-col gap-3">
        <Field label="Recherche" htmlFor="sm-search">
          <Input
            id="sm-search"
            ref={searchRef}
            value={filters.search}
            placeholder="N° / nom / tel"
            onChange={(event) => onChange({ search: event.target.value })}
          />
        </Field>

        <Field label="Affectation" htmlFor="sm-assigned">
          <Select
            id="sm-assigned"
            value={filters.assigned}
            onChange={(event) => onChange({ assigned: event.target.value as QueueFilters['assigned'] })}
          >
            <option value="all">Toutes</option>
            <option value="me">Mes commandes</option>
            <option value="unassigned">Non affectees</option>
          </Select>
        </Field>

        <Field label="Statut Woo" htmlFor="sm-woo-status">
          <Select
            id="sm-woo-status"
            value={filters.wooStatus}
            onChange={(event) => onChange({ wooStatus: event.target.value })}
          >
            <option value="all">Tous ({data?.wooStatusCounts === undefined ? '—' : Object.values(data.wooStatusCounts).reduce((a, b) => a + b, 0)})</option>
            {WOO_ORDER_STATUSES.map((status) => (
              <option key={status.slug} value={status.slug}>
                {getWooOrderStatus(status.slug)?.badgeLabel ?? status.name}
                {data?.wooStatusCounts?.[status.slug] === undefined
                  ? ''
                  : ` (${data.wooStatusCounts[status.slug]})`}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="mt-auto flex flex-col gap-2 border-t border-border pt-3">
        <Button variant="ghost" size="sm" onClick={onSync} disabled={syncing}>
          {syncing ? 'Synchronisation...' : '⟳ Synchroniser'}
        </Button>
        <p className="text-[11px] leading-relaxed text-ink-faint">
          {data?.lastSyncedAt === null || data?.lastSyncedAt === undefined
            ? 'Jamais synchronise.'
            : `Derniere sync ${relative(data.lastSyncedAt)}`}
        </p>
        <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-[11px] text-ink-faint">
          <dt>Page</dt>
          <dd className="text-right tabular-nums text-ink-soft">
            {data?.page ?? 1} / {Math.max(1, Math.ceil((data?.total ?? 0) / Math.max(1, data?.pageSize ?? 20)))}
          </dd>
          <dt>Montant file</dt>
          <dd className="text-right tabular-nums text-ink-soft">
            {money((data?.orders ?? []).reduce((sum, o) => sum + (o.total ?? 0), 0))}
          </dd>
        </dl>
      </div>
    </aside>
  );
}